import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import UpdateIcon from '@mui/icons-material/Update';
import TitleIcon from '@mui/icons-material/Title';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

export const defaultGameFilters = {
  minPlayers: '',
  maxPlayers: '',
  maxTime: '',
};

const maxTimeOptions = [
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 h' },
  { value: 90, label: '1 h 30' },
  { value: 120, label: '2 h' },
  { value: 180, label: '3 h' },
  { value: 240, label: '4 h' },
  { value: 300, label: '5 h' },
  { value: 360, label: '6 h' },
];
const playerOptions = Array.from({ length: 12 }, (_, index) => index + 1);
export const defaultGameSort = 'name';

export const defaultGameSortOptions = [
  { value: 'name', labelKey: 'shelf.sort.name', icon: TitleIcon },
  { value: 'recent', labelKey: 'shelf.sort.recent', icon: UpdateIcon },
  { value: 'timesPlayed', labelKey: 'shelf.sort.timesPlayed', icon: WhatshotIcon },
];

const PlayerIcon = ({ count }) =>
  count === 1 ? (
    <PersonIcon fontSize="small" />
  ) : count === 2 ? (
    <GroupIcon fontSize="small" />
  ) : (
    <GroupsIcon fontSize="small" />
  );

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 'min(50vh, 420px)',
    },
  },
};

const GameFiltersBar = ({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  sort = defaultGameSort,
  onSortChange,
  showSort = true,
  sortOptions = defaultGameSortOptions,
  defaultSort = defaultGameSort,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobilePicker, setMobilePicker] = useState(null);
  const hasActiveFilters = Boolean(searchQuery) || Object.values(filters).some(Boolean);
  const hasActiveState = hasActiveFilters || (showSort && sort !== defaultSort);

  const fieldConfigs = useMemo(
    () => [
      {
        key: 'minPlayers',
        label: t('shelf.filters.minPlayers'),
        value: filters.minPlayers,
        options: [
          { value: '', label: t('shelf.filters.any') },
          ...playerOptions.map((count) => ({
            value: count,
            label: `${count}+`,
            icon: <PlayerIcon count={count} />,
          })),
        ],
        renderValue: (value) => (value ? `${value}+` : t('shelf.filters.any')),
      },
      {
        key: 'maxPlayers',
        label: t('shelf.filters.maxPlayers'),
        value: filters.maxPlayers,
        options: [
          { value: '', label: t('shelf.filters.any') },
          ...playerOptions.map((count) => ({
            value: count,
            label: `${count}`,
            icon: <PlayerIcon count={count} />,
          })),
        ],
        renderValue: (value) => (value ? `${value}` : t('shelf.filters.any')),
      },
      {
        key: 'maxTime',
        label: t('shelf.filters.maxTime'),
        value: filters.maxTime,
        options: [
          { value: '', label: t('shelf.filters.any') },
          ...maxTimeOptions.map((option) => ({
            ...option,
            icon: <AccessTimeIcon fontSize="small" />,
          })),
        ],
        renderValue: (value) =>
          maxTimeOptions.find((option) => Number(option.value) === Number(value))?.label ||
          t('shelf.filters.any'),
      },
      ...(showSort
        ? [
            {
              key: 'sort',
              label: t('shelf.sort.label'),
              value: sort,
              options: sortOptions.map(({ value, labelKey, icon: Icon }) => ({
                value,
                label: t(labelKey),
                icon: Icon ? <Icon fontSize="small" /> : null,
              })),
              renderValue: (value) =>
                t(sortOptions.find((option) => option.value === value)?.labelKey || 'shelf.sort.name'),
            },
          ]
        : []),
    ],
    [filters.maxPlayers, filters.maxTime, filters.minPlayers, showSort, sort, sortOptions, t]
  );

  const handleFilterChange = (field) => (event) => {
    onFiltersChange({ ...filters, [field]: event.target.value });
  };

  const updateField = (field, value) => {
    if (field === 'sort') {
      onSortChange?.(value);
      return;
    }

    onFiltersChange({ ...filters, [field]: value });
  };

  const clearFilters = () => {
    onSearchChange('');
    onFiltersChange(defaultGameFilters);
    onSortChange?.(defaultSort);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr 1fr',
          md: showSort
            ? 'minmax(220px, 1.6fr) repeat(4, minmax(0, 1fr)) auto'
            : 'minmax(220px, 1.6fr) repeat(3, minmax(0, 1fr)) auto',
        },
        gap: 1.25,
        alignItems: 'center',
      }}
    >
      <TextField
        type="search"
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={searchPlaceholder || t('shelf.searchPlaceholder')}
        sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {fieldConfigs.map((field) => (
        isMobile ? (
          <Button
            key={field.key}
            variant="outlined"
            endIcon={<ExpandMoreIcon />}
            onClick={() => setMobilePicker(field.key)}
            sx={{
              justifyContent: 'space-between',
              minWidth: 0,
              px: 1.5,
              textTransform: 'none',
              '& .MuiButton-endIcon': { ml: 0.5 },
            }}
          >
            <Box sx={{ minWidth: 0, textAlign: 'left' }}>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {field.label}
              </Typography>
              <Typography fontWeight={800} noWrap>
                {field.renderValue(field.value)}
              </Typography>
            </Box>
          </Button>
        ) : (
          <FormControl key={field.key} size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              label={field.label}
              value={field.value}
              onChange={field.key === 'sort' ? (event) => onSortChange?.(event.target.value) : handleFilterChange(field.key)}
              renderValue={field.renderValue}
              MenuProps={selectMenuProps}
            >
              {field.options.map((option) => (
                <MenuItem key={option.value || 'any'} value={option.value}>
                  {option.icon ? (
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {option.icon}
                    </ListItemIcon>
                  ) : null}
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      ))}
      <Button
        variant="outlined"
        disabled={!hasActiveState}
        onClick={clearFilters}
        sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
      >
        {t('shelf.filters.clear')}
      </Button>
      <Drawer
        anchor="bottom"
        open={Boolean(mobilePicker)}
        onClose={() => setMobilePicker(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            bgcolor: 'background.default',
            overflow: 'hidden',
          },
        }}
      >
        {(() => {
          const field = fieldConfigs.find((item) => item.key === mobilePicker);
          if (!field) return null;

          return (
            <Box sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {field.key === 'sort' ? <SortIcon color="primary" /> : null}
                  <Typography variant="h3">{field.label}</Typography>
                </Stack>
                <Button
                  variant="text"
                  startIcon={<CloseIcon />}
                  onClick={() => setMobilePicker(null)}
                >
                  {t('common.close')}
                </Button>
              </Stack>
              <List
                disablePadding
                sx={{
                  maxHeight: '50vh',
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  pr: 0.5,
                }}
              >
                {field.options.map((option) => {
                  const selected = String(field.value) === String(option.value);

                  return (
                    <ListItemButton
                      key={option.value || 'any'}
                      selected={selected}
                      onClick={() => {
                        updateField(field.key, option.value);
                        setMobilePicker(null);
                      }}
                      sx={{
                        borderRadius: 2,
                        mb: 0.75,
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'rgba(15, 76, 69, 0.08)' : 'background.paper',
                      }}
                    >
                      {option.icon ? (
                        <ListItemIcon sx={{ minWidth: 38 }}>
                          {option.icon}
                        </ListItemIcon>
                      ) : null}
                      <ListItemText
                        primary={option.label}
                        primaryTypographyProps={{ fontWeight: selected ? 900 : 700 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })()}
      </Drawer>
    </Box>
  );
};

export default GameFiltersBar;
